// utils/mapGroups.js
import { extractSegmentPoints } from "./mapCoordinatesUtils";
import { coordKey } from "./mapCoordinatesUtils"; // or keep in same file

/**
 * Build de-duplicated marker groups from the raw segments.
 *
 * @param {Array<object>} segments
 * @returns {Array<object>} markerGroups
 *
 * markerGroup shape:
 * {
 *   key: string,
 *   lng: number,
 *   lat: number,
 *   points: [ { role, segment, index } ],
 *   departures: [...],
 *   arrivals: [...],
 *   pickups: [...],
 *   returns: [...],
 *   starts: [...],
 *   ends: [...],
 *   singles: [...],
 *   anchorSegment: object | undefined
 * }
 */
export function buildMarkerGroupsFromSegments(segments = []) {
  const groups = new Map();

  function addPoint(role, pt, segment, index) {
    if (!pt) return;
    const key = coordKey(pt);
    if (!key) return;

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        lng: pt.lng ?? pt.longitude,
        lat: pt.lat ?? pt.latitude,
        points: [],
        departures: [],
        arrivals: [],
        pickups: [],
        returns: [],
        starts: [],
        ends: [],
        singles: [],
      };
      groups.set(key, group);
    }

    const item = { role, segment, index };
    group.points.push(item);

    switch (role) {
      case "departure":
        group.departures.push(item);
        break;
      case "arrival":
        group.arrivals.push(item);
        break;
      case "pickup":
        group.pickups.push(item);
        break;
      case "return":
        group.returns.push(item);
        break;
      case "start":
        group.starts.push(item);
        break;
      case "end":
        group.ends.push(item);
        break;
      case "single":
        group.singles.push(item);
        break;
      default:
        break;
    }
  }

  segments.forEach((segment, index) => {
    const { departure, arrival, pickup, retrn, start, end, single } =
      extractSegmentPoints(segment);

    if (departure) addPoint("departure", departure, segment, index);
    if (arrival) addPoint("arrival", arrival, segment, index);
    if (pickup) addPoint("pickup", pickup, segment, index);
    if (retrn) addPoint("return", retrn, segment, index);
    if (start) addPoint("start", start, segment, index);
    if (end) addPoint("end", end, segment, index);
    if (single) addPoint("single", single, segment, index);
  });

  // choose an "anchor" segment per marker for basic metadata / popup title
  const result = [];
  for (const group of groups.values()) {
    const anchorItem =
      group.departures[0] ??
      group.singles[0] ??
      group.arrivals[0] ??
      group.pickups[0] ??
      group.returns[0] ??
      group.starts[0] ??
      group.ends[0];

    group.anchorSegment = anchorItem?.segment;
    result.push(group);
  }

  return result;
}

/**
 * Group markerGroups into clusterGroups based on precision.
 *
 * @param {Array<object>} markerGroups
 * @param {number} precision – how many decimals to keep when grouping
 * @returns {Array<object>} clusterGroups
 */
export function buildClusterGroupsFromMarkerGroups(
  markerGroups = [],
  precision = 1
) {
  const clustersMap = new Map();

  for (const mg of markerGroups) {
    if (!Number.isFinite(mg.lng) || !Number.isFinite(mg.lat)) continue;

    const key = coordKey({ lng: mg.lng, lat: mg.lat }, precision);
    if (!key) continue;

    let cluster = clustersMap.get(key);
    if (!cluster) {
      cluster = {
        id: key,
        lng: mg.lng,
        lat: mg.lat,
        members: [],
      };
      clustersMap.set(key, cluster);
    }

    cluster.members.push(mg);
  }

  return Array.from(clustersMap.values());
}
